import { useEffect } from "react";
import { Link } from "@/lib/router-compat";
import { Phone, Check } from "lucide-react";
import Header from "@/components/site/Header";
import SEOHead from "@/components/SEOHead";
import Footer from "@/components/site/Footer";
import FloatingCTAs from "@/components/site/FloatingCTAs";
import { useInsuranceModal } from "@/components/site/InsuranceModalContext";

const PHONE = "856-200-3127";
const TEL = "tel:8562003127";
const CANONICAL = "https://cherryhillrecoverycenter.com/iop-with-housing-options-nj";

const beneficiaries = [
  {
    lead: "Transitioning from residential or inpatient treatment:",
    body: "Patients completing detox, residential, or inpatient programs often need a step-down environment that provides more support than returning home immediately but less intensity than inpatient care. IOP with sober housing is the most common and most clinically effective step-down pathway.",
  },
  {
    lead: "Unstable or triggering home environments:",
    body: "If returning home means returning to a household where substances are present, where relationships are volatile, or where there are few supports for sobriety — sober living alongside IOP provides the separation needed to build early recovery on stable ground.",
  },
  {
    lead: "Limited family or social support:",
    body: "Not everyone has family or friends who can provide the day-to-day support that early recovery requires. Structured sober housing fills that gap — providing peer community, accountability, and a consistent environment of support.",
  },
  {
    lead: "Geographic distance from the treatment program:",
    body: "For patients traveling from outside the immediate Cherry Hill area for IOP treatment, local housing options can make consistent attendance practical where it would otherwise not be possible.",
  },
  {
    lead: "High relapse risk in early recovery:",
    body: "Patients with a history of multiple treatment attempts, strong environmental triggers, or significant instability benefit from the added accountability and containment that sober living provides alongside clinical programming.",
  },
];

const housingChecklist = [
  "Clean, substance-free environment with enforced house rules",
  "Random drug testing policy",
  "Peer community of residents in active recovery",
  "Structured expectations — curfews, meeting attendance, chores, employment requirements",
  "House manager or oversight with accountability",
  "Located in a safe neighborhood away from using environments",
  "New Jersey Association of Recovery Residences (NARR) certification preferred",
];

const questions = [
  "Does the IOP program have established relationships with local sober living homes, or will I need to find housing independently?",
  "Is the sober living home certified by the New Jersey Association of Recovery Residences or a comparable credentialing body?",
  "How does the housing provider handle residents who relapse — is there a clinical response or immediate removal?",
  "Are there scheduling options in the IOP — morning, evening, or virtual — that fit around work or other daytime commitments?",
  "What are the house rules and expectations for residents — curfews, employment requirements, meeting attendance?",
  "How does the housing cost work — is it covered by insurance or private pay, and what is the weekly rate?",
  "How long are residents typically expected to stay in the sober living home — is there a minimum or maximum duration?",
];

const tableRows = [
  ["Treatment Location", "On-site 24/7", "Off-site, 3–4 days/week"],
  ["Living", "At facility", "Sober living home"],
  ["Intensity", "Highest level", "Moderate — structured but flexible"],
  ["Daily Life", "Fully managed", "More autonomy — work/school possible"],
  ["Best For", "Highest acuity patients", "Step-down from residential or unstable home environment"],
  ["Cost", "Higher", "Significantly lower"],
];

const relatedPosts = [
  {
    title: "How IOP Programs Support Addiction Recovery Without Full Hospitalization",
    href: "/how-iop-programs-support-addiction-recovery-nj",
  },
  {
    title: "Evening IOP: Balancing Daily Life and Recovery",
    href: "/evening-iop-new-jersey",
  },
  {
    title: "What to Expect in a Partial Hospitalization Program",
    href: "/php-cherry-hill-nj",
  },
];

const tocItems = [
  { id: "what-is", label: "What Is IOP with Housing?" },
  { id: "who-benefits", label: "Who Benefits Most?" },
  { id: "what-housing-looks-like", label: "What Does Recovery Housing Look Like?" },
  { id: "logistics", label: "Sober Housing Logistics" },
  { id: "clinical-case", label: "The Clinical Case" },
  { id: "our-approach", label: "Our Approach" },
  { id: "iop-vs-residential", label: "IOP vs Residential" },
  { id: "questions-to-ask", label: "Questions to Ask" },
  { id: "is-iop-right", label: "Is IOP Right for You?" },
];

const TODAY = new Date().toISOString().split("T")[0];

const BlogIOPWithHousingOptionsNJ = () => {
  const { open: openInsurance } = useInsuranceModal();

  
  return (
    <div className="min-h-screen bg-background">
      <SEOHead title={"IOP with Housing in NJ | Sober Living + Intensive Outpatient"} description={"Need IOP with housing near you? Cherry Hill Recovery Center pairs intensive outpatient treatment with sober-living housing in South Jersey. Call 856-200-3127."} canonical={CANONICAL} />
      <Header />

      <main className="pt-[120px]">
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="bg-[#FAF4F0] border-b border-[#F0E0E0]">
          <div className="container py-3 text-sm flex items-center gap-2 flex-wrap">
            <Link to="/" className="text-[#8B1A1A] hover:underline">Home</Link>
            <span className="text-[#888]">›</span>
            <Link to="/blog" className="text-[#8B1A1A] hover:underline">Blog</Link>
            <span className="text-[#888]">›</span>
            <span className="text-[#2C2C2C]">IOP with Housing Options in NJ</span>
          </div>
        </nav>

        <section className="bg-white py-12">
          <div className="container grid lg:grid-cols-12 gap-10">
            {/* Article */}
            <article className="lg:col-span-8">
              {/* Post Header */}
              <header className="mb-8">
                <span
                  className="inline-block rounded-full px-3.5 py-1 text-xs font-semibold mb-4"
                  style={{ background: "#FAF4F0", color: "#8B1A1A" }}
                >
                  IOP &amp; PHP
                </span>
                <h1
                  className="font-display font-bold mb-5"
                  style={{ color: "#8B1A1A", fontSize: "clamp(28px, 4vw, 40px)", lineHeight: 1.2 }}
                >
                  IOP with Housing in New Jersey — Intensive Outpatient Plus Sober Living
                </h1>
                <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm mb-3" style={{ color: "#555" }}>
                  <span><strong>Published:</strong> February 19, 2024</span>
                  <span><strong>Updated:</strong> {new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</span>
                  <span><strong>Author:</strong> Cherry Hill Recovery Center Clinical Team</span>
                  <span>7 min read</span>
                </div>
                <p className="italic" style={{ color: "#888888", fontSize: "13px" }}>
                  Medically reviewed by the clinical team at Cherry Hill Recovery Center
                </p>
                <hr className="mt-6" style={{ borderColor: "#F0E0E0" }} />
              </header>

              {/* Opening */}
              <p className="text-[17px] leading-relaxed mb-8" style={{ color: "#2C2C2C" }}>
                For many people in addiction recovery, the hardest part is not the treatment program itself — it is what happens outside of it. Coming home to an unstable living environment, a household where substance use is still present, or a situation without structure or accountability can undermine even the best clinical programming. IOP with housing options addresses this directly — combining the structured clinical support of <Link to="/iop-cherry-hill-nj" className="underline" style={{ color: "#8B1A1A" }}>Intensive Outpatient treatment</Link> with stable, recovery-supportive housing so that both dimensions of recovery are addressed at once.
              </p>

              {/* What Is */}
              <h2 id="what-is" className="font-display font-bold mt-10 mb-4 scroll-mt-32" style={{ color: "#8B1A1A", fontSize: "28px" }}>
                What Is IOP with Housing?
              </h2>
              <p className="text-[16px] leading-relaxed mb-5" style={{ color: "#2C2C2C" }}>
                <Link to="/iop-cherry-hill-nj" className="underline" style={{ color: "#8B1A1A" }}>IOP — Intensive Outpatient Program</Link> — is a structured addiction treatment format that provides clinical care typically three to four days per week, approximately three hours per session, while allowing patients to live at home or in a community setting rather than in a residential facility. It provides meaningful clinical support — individual therapy, group counseling, <Link to="/psychiatric-evaluation-nj" className="underline" style={{ color: "#8B1A1A" }}>psychiatric evaluation</Link>, and skill building — without requiring patients to put their lives entirely on hold.
              </p>
              <p className="text-[16px] leading-relaxed mb-5" style={{ color: "#2C2C2C" }}>
                When IOP is combined with housing — meaning the patient lives in a structured, recovery-supportive living environment during or alongside their outpatient program — it creates a comprehensive recovery framework that addresses both the clinical and the environmental dimensions of addiction. The two work together: the IOP provides the clinical support, and the housing provides the stable, substance-free environment that makes that clinical work most effective.
              </p>

              {/* Who Benefits */}
              <h2 id="who-benefits" className="font-display font-bold mt-10 mb-4 scroll-mt-32" style={{ color: "#8B1A1A", fontSize: "28px" }}>
                Who Benefits Most From IOP with Housing?
              </h2>
              <p className="text-[16px] leading-relaxed mb-5" style={{ color: "#2C2C2C" }}>
                Not everyone who attends IOP needs a housing component — many patients have stable, supportive home environments that are conducive to recovery. But for a significant portion of patients, the living situation is as much a part of the clinical challenge as the addiction itself.
              </p>
              <p className="text-[16px] leading-relaxed mb-5" style={{ color: "#2C2C2C" }}>
                IOP with housing tends to be most beneficial for patients in the following situations:
              </p>
              <ul className="space-y-4 mb-6 list-none pl-0">
                {beneficiaries.map((b) => (
                  <li key={b.lead} className="flex gap-3">
                    <span
                      className="flex-shrink-0 mt-2 w-2.5 h-2.5 rounded-full"
                      style={{ background: "#8B1A1A" }}
                      aria-hidden="true"
                    />
                    <span className="text-[16px] leading-relaxed" style={{ color: "#2C2C2C" }}>
                      <strong style={{ color: "#6B1414" }}>{b.lead}</strong> {b.body}
                    </span>
                  </li>
                ))}
              </ul>

              {/* What recovery housing looks like */}
              <h2 id="what-housing-looks-like" className="font-display font-bold mt-10 mb-4 scroll-mt-32" style={{ color: "#8B1A1A", fontSize: "28px" }}>
                What Does Recovery-Supportive Housing Look Like?
              </h2>
              <p className="text-[16px] leading-relaxed mb-5" style={{ color: "#2C2C2C" }}>
                Recovery housing — also commonly called sober living homes, Oxford Houses, or transitional living — varies considerably in structure and quality. At its best, recovery housing provides a clean, substance-free living environment with peer community, house rules that support sobriety, accountability measures such as random drug testing, and a culture of mutual support among residents who are all working toward the same goal.
              </p>
              <p className="text-[16px] leading-relaxed mb-6" style={{ color: "#2C2C2C" }}>
                Most recovery housing operates independently of treatment programs — residents attend their IOP or other clinical programming separately and return to the house afterward. Some treatment programs have formal relationships with specific housing providers. Others provide referrals to vetted sober living options in the area. The key is that the housing and the treatment are both working in the same direction — reinforcing rather than competing with each other.
              </p>

              <aside
                role="note"
                className="rounded-lg p-6 mb-8"
                style={{ background: "#FAF4F0", borderLeft: "4px solid #8B1A1A" }}
              >
                <h3 className="font-bold mb-3" style={{ color: "#8B1A1A", fontSize: "15px" }}>
                  What to Look for in Recovery Housing
                </h3>
                <ul className="space-y-2.5 list-none pl-0">
                  {housingChecklist.map((item) => (
                    <li key={item} className="flex gap-3">
                      <Check className="h-5 w-5 flex-shrink-0 mt-0.5" style={{ color: "#8B1A1A" }} aria-hidden="true" />
                      <span className="text-[15px]" style={{ color: "#2C2C2C" }}>{item}</span>
                    </li>
                  ))}
                </ul>
              </aside>

              {/* Sober housing logistics */}
              <h2 id="logistics" className="font-display font-bold mt-10 mb-4 scroll-mt-32" style={{ color: "#8B1A1A", fontSize: "28px" }}>
                Sober Housing Logistics — Cost, Move-In, Rules and Transportation
              </h2>
              <p className="text-[16px] leading-relaxed mb-5" style={{ color: "#2C2C2C" }}>
                Most of the questions we get about IOP with housing are practical rather than clinical. Here is how the arrangement actually works in South Jersey, in the order people usually need to know it.
              </p>
              <h3 className="font-bold mt-6 mb-2" style={{ color: "#6B1414", fontSize: "20px" }}>How housing and treatment are billed separately</h3>
              <p className="text-[16px] leading-relaxed mb-5" style={{ color: "#2C2C2C" }}>
                Your clinical care and your housing are two separate arrangements. Insurance covers the treatment side — the <Link to="/iop-cherry-hill-nj" className="underline" style={{ color: "#8B1A1A" }}>IOP</Link> or <Link to="/php-cherry-hill-nj" className="underline" style={{ color: "#8B1A1A" }}>PHP</Link> programming, psychiatric care and medication management — and our admissions team verifies those benefits at no cost before you start. Sober living homes almost always bill residents directly, typically as a weekly rate that covers rent, utilities and house oversight. Rates in the Cherry Hill and South Jersey area generally fall in a modest weekly range, and homes will tell you exactly what is included before you commit. Some homes ask for a first-week payment plus a deposit at move-in; many will work with residents who are employed or about to be.
              </p>
              <h3 className="font-bold mt-6 mb-2" style={{ color: "#6B1414", fontSize: "20px" }}>How fast you can move in</h3>
              <p className="text-[16px] leading-relaxed mb-5" style={{ color: "#2C2C2C" }}>
                Placement depends on bed availability rather than paperwork, so timing varies. In practice, patients coming out of detox or residential care are usually the fastest to place because we start the housing conversation while they are still in treatment rather than on discharge day. Bring a photo ID, your insurance card, any current prescriptions and a short list of what you can pay weekly — that is normally enough for a house to make a decision the same day it has an opening. If you are being referred out for withdrawal management first, our <Link to="/detox-referral-coordination-nj" className="underline" style={{ color: "#8B1A1A" }}>detox coordination team</Link> handles the sequencing so housing is lined up before you finish.
              </p>
              <h3 className="font-bold mt-6 mb-2" style={{ color: "#6B1414", fontSize: "20px" }}>What daily life in the house looks like</h3>
              <p className="text-[16px] leading-relaxed mb-5" style={{ color: "#2C2C2C" }}>
                Expect a curfew, random drug and alcohol testing, shared chores, a requirement to attend your clinical program, and an expectation that you are working, in school or actively looking within the first few weeks. Rooms are usually shared. Guests, overnight absences and car use are governed by house rules rather than clinical rules, so read them before you sign. <Link to="/medication-assisted-treatment-nj" className="underline" style={{ color: "#8B1A1A" }}>Medication-assisted treatment</Link> — Suboxone, Sublocade or Vivitrol — is permitted in most reputable homes, but not all of them, so ask directly if MAT is part of your plan. Ask too what happens after a relapse: the better homes have a clinical response and a path back rather than an immediate removal.
              </p>
              <h3 className="font-bold mt-6 mb-2" style={{ color: "#6B1414", fontSize: "20px" }}>Getting to group each day</h3>
              <p className="text-[16px] leading-relaxed mb-5" style={{ color: "#2C2C2C" }}>
                Our building at 1930 Marlton Pike East sits on Route 70 in Cherry Hill, close to Route 73 and I-295, with on-site parking. Most residents drive, carpool with housemates attending the same group, or use NJ Transit bus service along the Route 70 corridor. If transportation is the sticking point on a given day, <Link to="/virtual-iop-new-jersey" className="underline" style={{ color: "#8B1A1A" }}>Virtual IOP</Link> lets you attend by secure video from the house instead of missing the session. Evening groups are available for residents who are working days — see <Link to="/evening-iop-new-jersey" className="underline" style={{ color: "#8B1A1A" }}>Evening IOP</Link>.
              </p>
              <h3 className="font-bold mt-6 mb-2" style={{ color: "#6B1414", fontSize: "20px" }}>How long people stay</h3>
              <p className="text-[16px] leading-relaxed mb-6" style={{ color: "#2C2C2C" }}>
                Most residents stay in sober housing longer than they stay in intensive treatment — commonly three to six months, well past the 8-to-12 weeks of IOP — because the point of the house is to hold environmental stability while new routines set. You can extend as long as you are meeting house expectations. For a fuller picture of the housing side, see our <Link to="/recovery-housing-nj" className="underline" style={{ color: "#8B1A1A" }}>recovery housing in New Jersey</Link> page, or call <a href={TEL} className="underline font-semibold" style={{ color: "#8B1A1A" }}>{PHONE}</a> and we will walk through cost and availability for your situation.
              </p>


              {/* Clinical case */}
              <h2 id="clinical-case" className="font-display font-bold mt-10 mb-4 scroll-mt-32" style={{ color: "#8B1A1A", fontSize: "28px" }}>
                The Clinical Case for IOP with Housing
              </h2>
              <p className="text-[16px] leading-relaxed mb-5" style={{ color: "#2C2C2C" }}>
                Research on addiction treatment outcomes consistently shows that environmental stability is one of the strongest predictors of sustained recovery. Treatment gains made in a clinical setting are most likely to hold when the patient returns to an environment that supports them — and most likely to be lost when the environment actively works against recovery.
              </p>
              <p className="text-[16px] leading-relaxed mb-5" style={{ color: "#2C2C2C" }}>
                This is not a failure of willpower or clinical skill. It is the predictable result of putting a person who has just built new neurological and behavioral patterns back into the exact environment that reinforced the old ones. Recovery housing creates a buffer — a period of environmental stability during which new patterns can become genuinely established before the person faces the full complexity of their previous environment.
              </p>
              <p className="text-[16px] leading-relaxed mb-5" style={{ color: "#2C2C2C" }}>
                For patients stepping down from <Link to="/detox-referral-coordination-nj" className="underline" style={{ color: "#8B1A1A" }}>detox referral</Link> or residential treatment into IOP, this buffer period is particularly important. The early weeks of recovery are when relapse risk is highest — and when the clinical work of IOP is most critical. Having stable, supportive housing during exactly this window significantly improves the likelihood that the IOP investment produces lasting results.
              </p>

              {/* Our approach */}
              <h2 id="our-approach" className="font-display font-bold mt-10 mb-4 scroll-mt-32" style={{ color: "#8B1A1A", fontSize: "28px" }}>
                How Cherry Hill Recovery Center Approaches IOP with Housing
              </h2>
              <p className="text-[16px] leading-relaxed mb-5" style={{ color: "#2C2C2C" }}>
                Cherry Hill Recovery Center's <Link to="/iop-cherry-hill-nj" className="underline" style={{ color: "#8B1A1A" }}>Intensive Outpatient Program</Link> provides the clinical component of the IOP-plus-housing model — individual therapy, group counseling, psychiatric evaluation and <Link to="/medication-assisted-treatment-nj" className="underline" style={{ color: "#8B1A1A" }}>medication management</Link> where appropriate, and evidence-based skill building. Our clinical team assesses each patient's living situation as part of the initial intake process and helps identify whether a housing component would strengthen the patient's recovery plan.
              </p>
              <p className="text-[16px] leading-relaxed mb-5" style={{ color: "#2C2C2C" }}>
                For patients who need housing support, our admissions team can provide guidance on recovery housing options in the Cherry Hill and South Jersey area — including NARR-certified sober living homes, Oxford Houses, and other transitional living options. We work to ensure that the housing environment our patients are in while attending IOP is genuinely supportive of their recovery — not an afterthought.
              </p>
              <p className="text-[16px] leading-relaxed mb-5" style={{ color: "#2C2C2C" }}>
                If you are considering IOP and are not sure whether your current living situation will support your recovery — or if you are stepping down from residential treatment and need both IOP and housing arranged — call our admissions team at <a href={TEL} className="underline font-semibold" style={{ color: "#8B1A1A" }}>{PHONE}</a>. We will assess your situation honestly and help you understand what the right combination of support looks like for where you are.
              </p>

              {/* IOP vs Residential */}
              <h2 id="iop-vs-residential" className="font-display font-bold mt-10 mb-4 scroll-mt-32" style={{ color: "#8B1A1A", fontSize: "28px" }}>
                IOP with Housing vs Residential Treatment — Understanding the Difference
              </h2>
              <p className="text-[16px] leading-relaxed mb-6" style={{ color: "#2C2C2C" }}>
                A common point of confusion is the difference between residential addiction treatment — where patients live at the treatment facility — and IOP with housing, where patients live in a separate sober living environment and attend outpatient programming off-site.
              </p>

              <div className="overflow-x-auto mb-6">
                <table
                  className="w-full text-left border-collapse"
                  style={{ border: "1px solid #F0E0E0", borderRadius: 8, overflow: "hidden" }}
                >
                  <thead>
                    <tr style={{ background: "#8B1A1A", color: "#FAF4F0" }}>
                      <th scope="col" className="p-3 text-sm font-semibold">&nbsp;</th>
                      <th scope="col" className="p-3 text-sm font-semibold">Residential</th>
                      <th scope="col" className="p-3 text-sm font-semibold">IOP with Housing</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tableRows.map((row, i) => (
                      <tr key={row[0]} style={{ background: i % 2 === 0 ? "#FFFFFF" : "#FAF4F0" }}>
                        <th scope="row" className="p-3 text-sm font-semibold align-top" style={{ color: "#6B1414" }}>{row[0]}</th>
                        <td className="p-3 text-sm align-top" style={{ color: "#2C2C2C" }}>{row[1]}</td>
                        <td className="p-3 text-sm align-top" style={{ color: "#2C2C2C" }}>{row[2]}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <p className="text-[16px] leading-relaxed mb-5" style={{ color: "#2C2C2C" }}>
                For many patients, IOP with housing represents the ideal middle ground — providing meaningful clinical support and environmental stability without the full intensity and cost of residential treatment. For others, particularly those with high acuity or complex co-occurring conditions, residential treatment may be the more appropriate starting point — with IOP and sober living as the step-down pathway that follows. <Link to="/php-cherry-hill-nj" className="underline" style={{ color: "#8B1A1A" }}>Partial Hospitalization Program</Link> is another option for higher levels of structure short of residential.
              </p>

              {/* Questions to ask */}
              <h2 id="questions-to-ask" className="font-display font-bold mt-10 mb-4 scroll-mt-32" style={{ color: "#8B1A1A", fontSize: "28px" }}>
                Questions to Ask When Considering IOP with Housing
              </h2>
              <p className="text-[16px] leading-relaxed mb-5" style={{ color: "#2C2C2C" }}>
                If you are exploring IOP with housing as an option, here are the questions worth asking both the treatment program and the housing provider:
              </p>
              <ol className="space-y-4 mb-6 list-none pl-0 counter-reset">
                {questions.map((q, i) => (
                  <li key={i} className="flex gap-4">
                    <span
                      className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
                      style={{ background: "#8B1A1A", color: "#FAF4F0" }}
                      aria-hidden="true"
                    >
                      {i + 1}
                    </span>
                    <span className="text-[16px] leading-relaxed pt-1" style={{ color: "#2C2C2C" }}>{q}</span>
                  </li>
                ))}
              </ol>

              {/* Is IOP right for you */}
              <h2 id="is-iop-right" className="font-display font-bold mt-10 mb-4 scroll-mt-32" style={{ color: "#8B1A1A", fontSize: "28px" }}>
                Is IOP Right for You — With or Without Housing?
              </h2>
              <p className="text-[16px] leading-relaxed mb-5" style={{ color: "#2C2C2C" }}>
                Not everyone who needs addiction treatment needs a residential or housing component. IOP — including <Link to="/virtual-iop-new-jersey" className="underline" style={{ color: "#8B1A1A" }}>Virtual IOP</Link> — is appropriate as a standalone level of care for patients who have a stable, supportive home environment, a functioning daily life they need to maintain, moderate rather than severe addiction severity, and the internal and external support structure to attend programming consistently and maintain sobriety between sessions.
              </p>
              <p className="text-[16px] leading-relaxed mb-8" style={{ color: "#2C2C2C" }}>
                The housing component becomes clinically important when one or more of those conditions is not in place. The best way to assess what level of structure is right for your situation is a clinical intake assessment — which our admissions team can conduct by phone, honestly and without pressure, before you commit to anything. You can also <button onClick={openInsurance} className="underline font-semibold" style={{ color: "#8B1A1A" }}>verify your insurance</button> in just a few minutes.
              </p>

              {/* Bottom CTA */}
              <div
                className="rounded-xl p-7 text-center mb-12"
                style={{ background: "#FAF4F0", borderTop: "4px solid #8B1A1A" }}
              >
                <h3 className="font-display font-bold mb-3" style={{ color: "#8B1A1A", fontSize: "20px" }}>
                  Ready to Talk Through Your Options?
                </h3>
                <p className="text-[15px] leading-relaxed mb-5" style={{ color: "#2C2C2C" }}>
                  Cherry Hill Recovery Center's IOP program serves patients throughout South Jersey and the greater Philadelphia region. Whether you need IOP only or IOP with housing support — call our admissions team and we will help you understand what the right level of structure looks like for your specific situation.
                </p>
                <a
                  href={TEL}
                  aria-label={`Call Cherry Hill Recovery Center at ${PHONE}`}
                  className="inline-flex items-center gap-2 rounded-full font-bold px-8 py-4 mb-3 hover:opacity-90 transition-opacity"
                  style={{ background: "#8B1A1A", color: "#FAF4F0" }}
                >
                  <Phone className="h-5 w-5" /> Call {PHONE}
                </a>
                <div>
                  <Link to="/iop-cherry-hill-nj" className="font-semibold hover:underline" style={{ color: "#8B1A1A" }}>
                    Learn more about our IOP program →
                  </Link>
                </div>
              </div>

              {/* Related Articles */}
              <h2 className="font-display font-bold mt-10 mb-5" style={{ color: "#8B1A1A", fontSize: "26px" }}>
                Related Articles
              </h2>
              <div className="grid sm:grid-cols-3 gap-5">
                {relatedPosts.map((p) => (
                  <Link
                    key={p.title}
                    to={p.href}
                    className="block rounded-xl p-5 hover:shadow-md transition-shadow"
                    style={{ background: "#FFFFFF", border: "1px solid #F0E0E0", borderTop: "3px solid #8B1A1A" }}
                  >
                    <h3 className="font-semibold text-[15px] leading-snug" style={{ color: "#2C2C2C" }}>
                      {p.title}
                    </h3>
                    <span className="text-sm font-semibold mt-3 inline-block" style={{ color: "#8B1A1A" }}>
                      Read more →
                    </span>
                  </Link>
                ))}
              </div>
            </article>

            {/* Sidebar */}
            <aside className="lg:col-span-4">
              <div
                className="lg:sticky space-y-6"
                style={{ top: "120px" }}
              >
                {/* About */}
                <div
                  className="rounded-xl p-6"
                  style={{ background: "#FAF4F0", border: "1px solid #F0E0E0", borderTop: "4px solid #8B1A1A" }}
                >
                  <h3 className="font-display font-bold mb-2" style={{ color: "#8B1A1A", fontSize: "18px" }}>
                    Cherry Hill Recovery Center
                  </h3>
                  <p className="text-[13px] leading-relaxed mb-4" style={{ color: "#555555" }}>
                    Cherry Hill Recovery Center provides PHP, IOP, MAT, and dual-diagnosis addiction treatment in Cherry Hill, NJ — serving South Jersey and the greater Philadelphia region.
                  </p>
                  <a
                    href={TEL}
                    aria-label={`Call ${PHONE}`}
                    className="block w-full text-center rounded-full font-semibold py-3"
                    style={{ background: "#8B1A1A", color: "#FAF4F0" }}
                  >
                    Call {PHONE}
                  </a>
                </div>

                {/* In this article */}
                <nav
                  aria-label="In this article"
                  className="rounded-xl p-6"
                  style={{ background: "#FFFFFF", border: "1px solid #F0E0E0" }}
                >
                  <p className="font-bold mb-3" style={{ color: "#8B1A1A", fontSize: "13px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    In This Article
                  </p>
                  <ul className="space-y-2 list-none pl-0">
                    {tocItems.map((t) => (
                      <li key={t.id}>
                        <a
                          href={`#${t.id}`}
                          className="text-sm hover:underline block"
                          style={{ color: "#2C2C2C" }}
                        >
                          → {t.label}
                        </a>
                      </li>
                    ))}
                  </ul>
                </nav>

                {/* Verify Insurance */}
                <div
                  className="rounded-xl p-6 mb-4"
                  style={{ background: "#FFFFFF", borderLeft: "4px solid #8B1A1A", border: "1px solid #F0E0E0", borderLeftWidth: 4 }}
                >
                  <p className="font-bold mb-2" style={{ color: "#8B1A1A", fontSize: "13px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Learn More About Recovery Housing
                  </p>
                  <p className="text-[13px] mb-3" style={{ color: "#2C2C2C", lineHeight: 1.6 }}>
                    Optional housing support during PHP and IOP — what it is, who it's for, and how to know if it's right for you.
                  </p>
                  <Link
                    to="/recovery-housing-nj"
                    className="inline-block font-semibold text-[13px]"
                    style={{ color: "#8B1A1A", textDecoration: "none" }}
                  >
                    Recovery Housing in NJ →
                  </Link>
                </div>
                <div
                  className="rounded-xl p-6"
                  style={{ background: "#FAF4F0", border: "1px solid #F0E0E0" }}
                >
                  <p className="font-bold mb-2" style={{ color: "#8B1A1A", fontSize: "13px", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Most Insurance Accepted
                  </p>
                  <p className="text-[12px] mb-4" style={{ color: "#555555" }}>
                    Find out what your plan covers before you commit to anything.
                  </p>
                  <button
                    onClick={openInsurance}
                    aria-label="Verify my insurance"
                    className="block w-full text-center rounded-full font-semibold py-3 border-2"
                    style={{ borderColor: "#8B1A1A", color: "#8B1A1A", background: "transparent" }}
                  >
                    Verify My Insurance
                  </button>
                </div>
              </div>
            </aside>
          </div>
        </section>
      </main>

      <Footer />
      <FloatingCTAs />
    </div>
  );
};

export default BlogIOPWithHousingOptionsNJ;
